import { gql } from '@apollo/client';
export const StatPermissionsBase = gql `
    query StatPermissionsBase($siteSetId: SiteSetId!, $institutionSetId: InstitutionSetId!, $employeeId: EmployeeId) {
        childDevelopment {
            resources {
                managementFlags(siteSetId: $siteSetId) {
                    canView
                    canEdit
                }
            }
        }

        management {
            employees(siteSetId: $siteSetId) {
                canView
                canEdit
                canCreate
                canManageRolesAndPermissions
                canAssignRoles

                contractData {
                    canView
                    canEdit
                }
            }

            ageGroups(siteSetId: $siteSetId) {
                canView
                canEdit
                canEditCapacityFactor
                settings {
                    canView
                    canEdit
                    canEditCapacityFactor
                }
            }

            careGroups(siteSetId: $siteSetId) {
                canView
                canEdit
                settings {
                    canView
                    canEdit
                }
            }

            careRules(siteSetId: $siteSetId) {
                canView
                canEdit
            }

            careSummary(siteSetId: $siteSetId) {
                canViewGraph
                canViewIssuesAndCsv
                canViewCsv
            }
        }

        additionalNeedTypes {
            management(siteSetId: $siteSetId) {
                canView
                canEdit
            }
            childOverview(siteSetId: $siteSetId) {
                canView
            }
            childListPermissions(siteSetId: $siteSetId) {
                canView
            }
        }

        staffhours {
            featureSettings {
                exactAttendance(siteSetId: $institutionSetId) {
                    canOptIn
                    isEnabled
                }
                leaveRangeSettings(siteSetId: $institutionSetId) {
                    enabled
                }
                staffLeave(siteSetId: $siteSetId) {
                    isEnabled
                    canOptIn
                    canOptOut
                }
                shiftplanner(siteSetId: $institutionSetId) {
                    isEnabled
                    canOptIn
                }
            }

            management {
                approval: approvalV2(siteSetId: $siteSetId) {
                    canViewAll
                    canApproveAndLockAll
                    canUnlockAll
                    managementFlags {
                        siteId
                        canView
                        canApproveAndLock
                        canUnlock
                    }
                }

                attendance: attendanceV2(siteSetId: $siteSetId, employeeId: $employeeId) {
                    canView
                    canEdit
                    siteSet {
                        canView
                        canEdit
                    }

                    dailyCsvExportURL {
                        canView
                    }

                    detailedCsvExportURL {
                        canView
                    }
                }

                totals: totalsV2(siteSetId: $siteSetId, employeeId: $employeeId) {
                    canView
                    csvExport {
                        canView
                    }
                }

                shiftplanner(siteSetId: $siteSetId) {
                    shift {
                        draft(siteSetId: $siteSetId) {
                            canView
                            canCreate
                            canEdit
                            canDelete
                            canBulkDelete
                        }

                        published: publishedV2(siteSetId: $siteSetId, employeeId: $employeeId) {
                            canView
                            canCreate
                            canEdit
                            canDelete
                            canBulkDelete
                        }
                    }
                    canView
                    canEdit
                }

                leave {
                    completed(siteSetId: $siteSetId, employeeId: $employeeId) {
                        canView
                        canCreate
                        canEdit
                        canDelete
                    }

                    pending(siteSetId: $siteSetId, employeeId: $employeeId) {
                        canApprove
                        canCreate
                        canDelete
                        canEdit
                        canReject
                        canView
                        canViewPendingTab
                    }

                    totals(siteSetId: $siteSetId, employeeId: $employeeId) {
                        canView
                    }
                }

                staffAttendance {
                    siteSet(siteSetId: $siteSetId) {
                        canView
                    }
                }

                staffLeaveSettings(siteSetId: $siteSetId) {
                    canView
                    canEdit
                }

                subtypes(siteSetId: $institutionSetId) {
                    canView
                    canCreate
                    canEdit
                    canDelete
                }

                leave {
                    subtype(siteSetId: $siteSetId) {
                        canView
                        canEdit
                    }

                    csvExportTotals(siteSetId: $siteSetId) {
                        canView
                    }

                    csvExportDetailed(siteSetId: $siteSetId) {
                        canView
                    }

                    csvExportOverview(siteSetId: $siteSetId) {
                        canView
                    }

                    bradfordFactor(siteSetId: $siteSetId) {
                        canView
                    }

                    conflicts(siteSetId: $siteSetId) {
                        canView
                    }
                }

                workTags(siteSetId: $siteSetId) {
                    canView
                    canCreate
                    canEdit
                    canDelete
                }

                occupancyBudgets(siteSetId: $siteSetId) {
                    canView
                    canEdit
                }

                contractedHours: contractedHoursV2(siteSetId: $siteSetId, employeeId: $employeeId) {
                    contractedHours {
                        canView
                        canEdit
                    }
                    workAvailability {
                        canView
                        canEdit
                    }
                    bankHours {
                        canView
                        canEdit
                    }
                    changeLog {
                        canView
                        canEdit
                    }
                }

                qualificationTypeAssignment(siteSetId: $siteSetId, employeeId: $employeeId) {
                    canCreate
                    canDelete
                    canEdit: canUpdate
                    canView
                }

                credentialsAssignment(siteSetId: $siteSetId, employeeId: $employeeId) {
                    canCreate
                    canDelete
                    canEdit: canUpdate
                    canView
                }

                credentials(siteSetId: $siteSetId) {
                    canView
                    canCreate
                    canEdit: canUpdate
                    canDelete
                }

                qualificationType(siteSetId: $siteSetId) {
                    canCreate
                    canDelete
                    canEdit: canUpdate
                    canView
                }
            }

            shiftPlannerOnboarding(siteSetId: $institutionSetId) {
                recommendsChildPlansSetup
                recommendsNewWorkTagColors
            }
        }

        attendance {
            featureSettings {
                inquiry(siteSetId: $siteSetId) {
                    hideEnquiryCSVDownload
                    hideNewEnquiryButton
                }
                milkReport(siteSetId: $institutionSetId) {
                    enabled
                }
                kinderConnect(siteSetId: $institutionSetId) {
                    enabled
                }
            }

            management {
                childAttendance(siteSetId: [$siteSetId]) {
                    flags {
                        canExportCsv
                    }
                }

                onboardingTeamContacts(siteSetId: $siteSetId) {
                    canView
                    canUpdate
                }
                customRegistrationForm {
                    bySiteSetId(siteSetId: $siteSetId) {
                        canView
                        canEdit
                        canDelete
                        canDownloadResponses
                        canDuplicate
                        canEditName
                        canEditSuccessMessage
                        canPublish
                        canCreate
                        canCreateFollowUpForm
                        canUnpublish
                        canShare
                        canToggleNotifications
                        canToggleConfirmationEmail
                        canManageSubmissionKeys
                        canSendEmails
                        canViewResponses
                        canSetFeeAmount
                        canViewFeeSection
                    }

                    enrollment(siteSetId: $siteSetId) {
                        canView
                        canCreate
                    }

                    interest(siteSetId: $siteSetId) {
                        canView
                        canCreate
                    }

                    registration(siteSetId: $siteSetId) {
                        canView
                        canCreate
                    }
                }

                customFte(siteSetId: $institutionSetId) {
                    canView
                    canEdit
                }

                orgInquiries(siteSetIds: [$siteSetId]) {
                    siteId
                    canCreate
                    canView
                    canUpdate
                    canDelete
                    canEnrollChildren
                    canMoveInquiriesOut
                    canCreateActions
                    canViewActions
                    canUpdateActions
                    canDeleteActions
                }

                taskManagement {
                    taskList(siteSetId: $siteSetId) {
                        canView
                        canCreate
                        canUpdate
                        canDelete
                        canTogglePublishedState
                    }
                }
            }
        }
        careContractTemplates {
            managementFlags(siteSetId: $siteSetId) {
                canManage
            }
        }
        childProfile {
            careContracts {
                managementFlags(siteSetId: $siteSetId) {
                    canView
                    canCreate
                    canDelete
                    canViewFileUrls
                    canCreateFromTemplate
                }
            }
        }
    }
`;
